// @figma file=pkCGby54zXzTsccXvHqzO6 node=tbd name="Combobox" set="mytaible-shadcn-studio" verified=tbd

import { cva, type VariantProps } from 'class-variance-authority';
import { Check, ChevronsUpDown } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

// ─── Trigger Variants ─────────────────────────────────────────────────────────

const comboboxTriggerVariants = cva(
  [
    'flex w-full cursor-pointer items-center justify-between gap-2 whitespace-nowrap',
    'rounded-[var(--field-radius,var(--radius))] border border-input bg-input-bg text-foreground shadow-sm',
    'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background',
    'disabled:cursor-not-allowed disabled:opacity-50',
    '[&>span]:min-w-0 [&>span]:flex-1 [&>span]:truncate [&>span]:text-left',
  ],
  {
    variants: {
      size: {
        sm: 'h-8 px-3 text-base sm:text-xs',
        default: 'h-9 px-3 py-2 text-base sm:text-sm',
        lg: 'h-10 px-4 text-base',
      },
      shape: {
        rounded: 'rounded-[var(--field-radius,var(--radius))]',
        round: 'rounded-full',
      },
    },
    defaultVariants: { size: 'default', shape: 'rounded' },
  },
);

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ComboboxTriggerProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof comboboxTriggerVariants> {}

export interface ComboboxProps {
  options: Array<{ value: string; label: string }>;
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
}

// ─── ComboboxTrigger ──────────────────────────────────────────────────────────

const ComboboxTrigger = React.forwardRef<HTMLButtonElement, ComboboxTriggerProps>(
  ({ className, size, shape, type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(comboboxTriggerVariants({ size, shape }), className)}
      {...props}
    />
  ),
);
ComboboxTrigger.displayName = 'ComboboxTrigger';

// ─── Combobox ─────────────────────────────────────────────────────────────────

const Combobox = React.forwardRef<HTMLButtonElement, ComboboxProps>(
  (
    {
      options,
      value,
      onValueChange,
      placeholder = 'Select an option…',
      searchPlaceholder = 'Search…',
      emptyText = 'No results found.',
      disabled,
      className,
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);

    const selectedLabel = React.useMemo(
      () => options.find((o) => o.value === value)?.label,
      [options, value],
    );

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <ComboboxTrigger
            ref={ref}
            role="combobox"
            aria-expanded={open}
            aria-label={selectedLabel ?? placeholder}
            disabled={disabled}
            className={cn('w-48 font-normal', className)}
          >
            <span className={cn(!selectedLabel && 'text-muted-foreground')}>
              {selectedLabel ?? placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
          </ComboboxTrigger>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-0" aria-label={placeholder}>
          <Command>
            <CommandInput placeholder={searchPlaceholder} />
            <CommandList>
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={(currentValue) => {
                      onValueChange?.(currentValue === value ? '' : currentValue);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === option.value ? 'opacity-100' : 'opacity-0',
                      )}
                      aria-hidden="true"
                    />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  },
);
Combobox.displayName = 'Combobox';

export { Combobox, ComboboxTrigger, comboboxTriggerVariants };
